'use strict';

import { isArray, reverse } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import OTButton from './OTButton';
import Row from './Row';
import Span from './Span';

/**
 * Button Group acts as a wrapper for easier grouping and adjusting behaviour on mobile.
 * Button Group will divide given width to buttons specified as children.
 *
 * @param {Array}   children: the children of the component
 * @param {String}  type: can be given to determine type of all buttons inside group (see OtButton)
 * @param {Boolean} fullWidth: make button container take up the whole width of its' container
 * @param {Number}  expectedCount: expected (minimum) count of buttons for group. Useful in case of
 *                       dynamically loaded buttons.
 **/

const gridWidth = 12;

class OTButtonGroup extends React.Component {
  constructor() {
    super();
    this.state = {
      width: window.innerWidth,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowSizeChange);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange);
  }

  handleWindowSizeChange = () => {
    this.setState({ width: window.innerWidth });
  };

  render() {
    let {
      children,
      type = 'default',
      fullWidth = false,
      oneButtonPerRow = false,
      collapseOnMobile = false,
      expectedCount = 2,
      formulaButton,
    } = this.props;
    let { width } = this.state;
    let isMobile = width <= 780;
    let buttonWidth = oneButtonPerRow
      ? 12
      : Math.floor(gridWidth / Math.max(children.length || 1, expectedCount));
    let buttonSmWidth =
      collapseOnMobile && formulaButton
        ? 6
        : collapseOnMobile
        ? 12
        : buttonWidth;
    let buttonStyle = { textAlign: 'left' };

    if (isMobile && collapseOnMobile && !formulaButton) {
      buttonStyle = { ...buttonStyle, marginLeft: 0 };

      if (isArray(children)) {
        children = children.slice().reverse();
      }
    }

    let content = null;

    if (isArray(children)) {
      content = children.map((child, i) => {
        if (!child) {
          return null;
        }
        return oneButtonPerRow ? (
          <Row style={{ marginBottom: '5px' }}>
            <Span
              len={buttonWidth}
              sm-len={buttonSmWidth}
              key={i}
              style={buttonStyle}
            >
              {React.cloneElement(child, { type })}
            </Span>
          </Row>
        ) : formulaButton ? (
          <Span flex={1} sm-len={buttonSmWidth} key={i} style={buttonStyle}>
            {React.cloneElement(child, { type })}
          </Span>
        ) : (
          <Span
            len={buttonWidth}
            sm-len={buttonSmWidth}
            key={i}
            style={buttonStyle}
          >
            {React.cloneElement(child, { type })}
          </Span>
        );
      });
    } else {
      content = (
        <Span len={buttonWidth} sm-len={buttonWidth}>
          {React.cloneElement(children, { type })}
        </Span>
      );
    }

    return oneButtonPerRow ? (
      <div className="ot-button-container" style={{ width: '100%' }}>
        {content}
      </div>
    ) : fullWidth && formulaButton ? (
      <Row className="ot-button-container-formula" style={{ width: '100%' }}>
        {content}
      </Row>
    ) : fullWidth ? (
      <Row className="ot-button-container">{content}</Row>
    ) : (
      <div className="ot-button-container">
        <Row>{content}</Row>
      </div>
    );
  }
}

OTButtonGroup.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({ type: OTButton })),
    PropTypes.element,
  ]).isRequired,
};

export default OTButtonGroup;
